import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { FROM_EMAILS } from "const/emailSenders";
import { useLinkedAdminFields } from "utils/visaDocumentService";
import AdminFillFieldsForm, { missingRequiredAdminFields } from "./AdminFillFieldsForm";
import DocumentPreviewDialog from "./DocumentPreviewDialog";

const VISA_DEFAULT_FROM_EMAIL = "visa@kotharedu.com";

const SendSignatureRequestDialog = ({
  open,
  onClose,
  visaId,
  templateId,
  documentTitle,
  recipientEmail = "",
  onSubmit,
  loading = false,
  resend = false,
  hasFilledCopy = false,
  maraRequired = false,
  maraAgents = [],
  maraAgentsLoading = false,
  bankRequired = false,
  bankAccounts = [],
  bankAccountsLoading = false,
  adminFields = [],
  uppercaseSupported = false,
  uppercaseDefault = true,
}) => {
  const [note, setNote] = useState("");
  const [maraAgentId, setMaraAgentId] = useState("");
  const [bankAccountId, setBankAccountId] = useState("");
  const [fromEmail, setFromEmail] = useState(VISA_DEFAULT_FROM_EMAIL);
  const [adminValues, setAdminValues] = useState({});
  const [uppercaseValues, setUppercaseValues] = useState(true);
  // Snapshotted when the preview opens rather than rebuilt every render: the preview renders once
  // for the answers as they stood at that click, and a fresh object each render would re-render it
  // in a loop.
  const [previewPayload, setPreviewPayload] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setNote("");
      setMaraAgentId("");
      setBankAccountId("");
      setFromEmail(VISA_DEFAULT_FROM_EMAIL);
      setUppercaseValues(uppercaseSupported && uppercaseDefault);
      const defaults = {};
      adminFields.forEach((field) => {
        if (field.defaultValue) defaults[field.name] = field.defaultValue;
      });
      setAdminValues(defaults);
      setPreviewPayload(null);
      setConfirmOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && bankRequired && bankAccounts.length === 1) {
      setBankAccountId(bankAccounts[0].id);
    }
  }, [open, bankRequired, bankAccounts]);

  const noEligibleMara = maraRequired && !maraAgentsLoading && maraAgents.length === 0;
  const noEligibleBank = bankRequired && !bankAccountsLoading && bankAccounts.length === 0;

  const { data: linked } = useLinkedAdminFields(
    { visaId, templateId, maraAgentId, bankAccountId },
    { enabled: open && Boolean(maraAgentId || bankAccountId) }
  );
  const linkedFields = linked?.missing || [];
  // Values the picked agent/bank resolved, used only to drive conditional visibility (an exempt
  // agent's capacity box unhides its exempt-reason boxes). Never submitted: the send re-resolves
  // every linked value from the agent, so admin edits stay the only thing in adminValues.
  const resolvedLinkedValues = linked?.resolvedValues || {};
  const allAdminFields = [...adminFields, ...linkedFields];
  const visibilityValues = { ...resolvedLinkedValues, ...adminValues };

  const missingAdmin = missingRequiredAdminFields(allAdminFields, visibilityValues);
  const canSubmit =
    (!maraRequired || Boolean(maraAgentId)) &&
    (!bankRequired || Boolean(bankAccountId)) &&
    missingAdmin.length === 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setConfirmOpen(false);
    setPreviewPayload(null);
    onSubmit?.({
      note: note.trim(),
      maraAgentId: maraAgentId || null,
      bankAccountId: bankAccountId || null,
      fromEmail,
      fieldValues: adminValues,
      uppercaseValues: uppercaseSupported ? uppercaseValues : false,
    });
  };

  const openPreview = () => {
    if (!canSubmit) return;
    setConfirmOpen(false);
    setPreviewPayload({
      visaId,
      templateId,
      maraAgentId: maraAgentId || null,
      bankAccountId: bankAccountId || null,
      fieldValues: adminValues,
      uppercaseValues: uppercaseSupported ? uppercaseValues : false,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={allAdminFields.length ? "md" : "xs"} fullWidth>
      <DialogTitle className="font-semibold">
        {resend ? "Resend" : "Send"} "{documentTitle}"
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            We'll email {recipientEmail || "the client"} a secure link where they can fill in and
            sign this document — no login required.
          </Typography>
          {resend && hasFilledCopy && (
            <Typography variant="body2" color="text.secondary">
              Their previously filled copy stays available for download until they submit the new
              one.
            </Typography>
          )}

          {maraRequired && noEligibleMara && (
            <Alert severity="warning">
              This document requires a MARA agent, but no MARA agent tagged for Visa exists yet. Add
              one under Settings → Manage MARA before sending.
            </Alert>
          )}

          {maraRequired && !noEligibleMara && (
            <TextField
              select
              label="MARA agent"
              size="small"
              fullWidth
              required
              value={maraAgentId}
              onChange={(e) => setMaraAgentId(e.target.value)}
              disabled={maraAgentsLoading}
              helperText="This document requires a MARA agent's details and signature"
            >
              {maraAgents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.agentName}
                  {!agent.hasSignature ? " (no signature on file)" : ""}
                </MenuItem>
              ))}
            </TextField>
          )}

          {bankRequired && noEligibleBank && (
            <Alert severity="warning">
              This document requires a bank account, but none exist yet. Add one under Settings →
              Manage Bank Accounts before sending.
            </Alert>
          )}

          {bankRequired && !noEligibleBank && (
            <TextField
              select
              label="Bank account"
              size="small"
              fullWidth
              required
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              disabled={bankAccountsLoading}
              helperText="This document requires a bank account's payment details"
            >
              {bankAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.accountName} — BSB {account.bsb}, Acc. No. {account.accountNumber}
                </MenuItem>
              ))}
            </TextField>
          )}

          {uppercaseSupported && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={uppercaseValues}
                    onChange={(e) => setUppercaseValues(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Fill this form in BLOCK LETTERS"
                slotProps={{ typography: { fontSize: 14 } }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                Capitalises every answer, yours and the client's, on the signing page and in the
                final PDF.
              </Typography>
            </Box>
          )}

          <Autocomplete
            options={FROM_EMAILS}
            value={fromEmail}
            onChange={(_, val) => setFromEmail(val || VISA_DEFAULT_FROM_EMAIL)}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                label="Send From"
                size="small"
                fullWidth
                helperText="Sender address for this document email"
              />
            )}
            isOptionEqualToValue={(o, v) => o === v}
          />

          {allAdminFields.length > 0 && (
            <Box>
              <Divider sx={{ mb: 1.5 }} />
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Fill these fields before sending
                </Typography>
                {missingAdmin.length > 0 && (
                  <Typography variant="caption" color="warning.main">
                    {missingAdmin.length} still required
                  </Typography>
                )}
              </Stack>
              <Box sx={{ mb: 1.5 }} />
              {/* A government form maps to dozens of fields; keeping them in their own scroll area
                  means the send controls and the buttons stay reachable without paging past them. */}
              <Box
                sx={{
                  maxHeight: 420,
                  overflowY: "auto",
                  pr: 1.5,
                  py: 0.5,
                }}
              >
                <AdminFillFieldsForm
                  fields={allAdminFields}
                  values={adminValues}
                  visibilityContext={resolvedLinkedValues}
                  onChange={setAdminValues}
                  disabled={loading}
                  uppercase={uppercaseSupported && uppercaseValues}
                />
              </Box>
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          )}

          <TextField
            label="Message to client (optional)"
            helperText="Added to the email the client receives, above the document link"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a short message for the client…"
            autoFocus={!maraRequired && !bankRequired && allAdminFields.length === 0}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={openPreview}
          disabled={loading || !canSubmit}
          startIcon={<VisibilityIcon fontSize="small" />}
        >
          Preview
        </Button>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          disabled={loading || !canSubmit}
        >
          {loading ? "Sending…" : resend ? "Resend link" : "Send link"}
        </Button>
      </DialogActions>

      {/* Last stop before the client gets it. Preview sits beside Send rather than behind it, so
          checking the filled form is never more than one click away at the moment of sending. */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle className="font-semibold">
          {resend ? "Resend" : "Send"} to {recipientEmail || "the client"}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            "{documentTitle}" will be emailed to {recipientEmail || "the client"} with a secure link
            to fill in and sign. Preview it first if you want to check the details you filled in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={openPreview}
            disabled={loading}
            startIcon={<VisibilityIcon fontSize="small" />}
          >
            Preview
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? "Sending…" : resend ? "Resend link" : "Send link"}
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentPreviewDialog
        open={Boolean(previewPayload)}
        onClose={() => setPreviewPayload(null)}
        documentTitle={documentTitle}
        payload={previewPayload}
        onConfirmSend={handleSubmit}
        sending={loading}
      />
    </Dialog>
  );
};

export default SendSignatureRequestDialog;
